import type { ReactNode } from "react";
import { Box, Paper, Typography } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ErrorIcon from "@mui/icons-material/Error";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import VerifiedIcon from "@mui/icons-material/Verified";
import PaymentsIcon from "@mui/icons-material/Payments";
import StarsIcon from "@mui/icons-material/Stars";
import TimerIcon from "@mui/icons-material/Timer";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { BRAND_COLOR, LeadStatus, formatCurrency, formatDuration } from "../constants";

interface AnalyticsKpiCardsProps {
  data: LeadsManagementData;
}

interface KpiCardData {
  label: string;
  value: ReactNode;
  caption: string;
  icon: ReactNode;
  // Quality & Monetization cards are plain, non-interactive boxes — clicking
  // through would set qualified_by_brand/lead_scoring_min, but the backend
  // doesn't reliably filter by those yet, which made the cards look broken
  // rather than just inert. Omit onClick for a non-clickable card.
  onClick?: () => void;
  trackerId?: string;
}

function KpiCard({ kpi }: { kpi: KpiCardData }) {
  return (
    <Paper
      variant="outlined"
      onClick={kpi.onClick}
      title={kpi.onClick ? "View these leads" : undefined}
      data-tracker={kpi.trackerId}
      sx={{ p: "20px 22px", borderRadius: 2, cursor: kpi.onClick ? "pointer" : "default" }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 500, color: BRAND_COLOR }}>
          {kpi.label.toUpperCase()}
        </Typography>
        {kpi.icon}
      </Box>
      <Typography sx={{ fontSize: 34, fontWeight: 700, color: "#1a1a1a", mt: 1.25 }}>
        {kpi.value}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: "#9e9e9e", mt: 0.25 }}>{kpi.caption}</Typography>
    </Paper>
  );
}

export default function AnalyticsKpiCards({ data }: AnalyticsKpiCardsProps) {
  const { aggregations, totalFiltered, goToFilteredLeads } = data;

  const countFor = (status: LeadStatus) =>
    aggregations.byStatus.find((item) => item.status === status)?.count ?? 0;
  const pending = countFor("Pending");
  const reported = countFor("Success");
  const failed = countFor("Failure");
  const denominator = reported + failed;
  const successRate = denominator ? `${Math.round((reported / denominator) * 100)}%` : "—";

  // Quality & Monetization reads straight from the server-computed
  // aggregations (qualified_by_brand/income_from_brand/avg_lead_score/
  // avg_fill_time_seconds) — these reflect the full filtered result set,
  // the same way by_status/by_day/by_brand already do above.
  const { qualifiedByBrand, incomeFromBrand, avgLeadScore, avgFillTimeSeconds } = aggregations;
  const qualifiedRate =
    qualifiedByBrand.percentOfSuccessLeads !== null
      ? `${Math.round(qualifiedByBrand.percentOfSuccessLeads)}%`
      : "—";

  const kpis = [
    {
      label: "Total leads",
      value: totalFiltered,
      caption: "across all brands",
      icon: <GroupsIcon sx={{ color: BRAND_COLOR }} />,
      onClick: () => goToFilteredLeads({ status: "All" }),
      trackerId: "934",
    },
    {
      label: "Pending",
      value: pending,
      caption: "awaiting report",
      icon: <ScheduleIcon sx={{ color: "#E65100" }} />,
      onClick: () => goToFilteredLeads({ status: "Pending" }),
      trackerId: "935",
    },
    {
      label: "Reported",
      value: reported,
      caption: `${successRate} success rate`,
      icon: <TaskAltIcon sx={{ color: "#2E7D32" }} />,
      onClick: () => goToFilteredLeads({ status: "Success" }),
      trackerId: "936",
    },
    {
      label: "Failed",
      value: failed,
      caption: "report failed",
      icon: <ErrorIcon sx={{ color: "#C62828" }} />,
      onClick: () => goToFilteredLeads({ status: "Failure" }),
      trackerId: "937",
    },
  ];

  const qualityKpis: KpiCardData[] = [
    {
      label: "Qualified by brand",
      value: qualifiedByBrand.count,
      caption: `${qualifiedRate} of reported leads`,
      icon: <VerifiedIcon sx={{ color: "#2E7D32" }} />,
      onClick: () => goToFilteredLeads({ qualified: "Yes" }),
    },
    {
      label: "Income from brands",
      value: formatCurrency(incomeFromBrand.total),
      caption: `${formatCurrency(incomeFromBrand.avgPerSuccessLead)} avg. per reported lead`,
      icon: <PaymentsIcon sx={{ color: "#2E7D32" }} />,
    },
    {
      label: "Avg. lead score",
      value: avgLeadScore.average !== null ? avgLeadScore.average.toFixed(1) : "—",
      caption: `out of 10 · ${avgLeadScore.scoredLeadsCount} scored`,
      icon: <StarsIcon sx={{ color: BRAND_COLOR }} />,
    },
    {
      label: "Avg. fill time",
      value: formatDuration(avgFillTimeSeconds.averageSeconds),
      caption: "questionnaire completion",
      icon: <TimerIcon sx={{ color: BRAND_COLOR }} />,
    },
  ];

  return (
    <Box sx={{ mt: 3.5 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color: "#1a1a1a" }}>
        High Level KPI's
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 13, color: "#9e9e9e", mb: 1.75 }}>
        <AdsClickIcon sx={{ fontSize: 16, color: BRAND_COLOR }} />
        Click any KPI, bar or data point to open the matching leads.
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2.5 }}>
        {kpis.map((kpi) => (
          <KpiCard key={kpi.label} kpi={kpi} />
        ))}
      </Box>

      <Typography sx={{ fontSize: 22, fontWeight: 500, color: "#1a1a1a", mt: 3.75 }}>
        Quality &amp; Monetization
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#9e9e9e", mb: 1.75 }}>
        Based on brand feedback and the scoring engine.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2.5 }}>
        {qualityKpis.map((kpi) => (
          <KpiCard key={kpi.label} kpi={kpi} />
        ))}
      </Box>
    </Box>
  );
}
